// =============================================================================
// LEGAL & COOKIE MODALS
// =============================================================================
// Accessible, responsive modal dialogs for:
// 1. Privacy Policy (GDPR / CCPA compliant restaurant privacy notice)
// 2. Terms of Service (Reservations, cancellations, allergen liability)
// 3. Accessibility Statement (WCAG 2.1 AA commitments)
// 4. Granular Cookie Settings (Category toggles with save/accept/reject)
//
// All strings loaded dynamically via i18n locales (`ui.json`) across all languages.
// =============================================================================

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Restaurant } from '@webzite/content-schema';
import { useConsent, type ConsentState } from '../hooks/useConsent.js';
import { CloseIcon } from './icons.js';
import './LegalModal.css';

export type LegalModalType = 'privacy' | 'terms' | 'accessibility' | 'cookies' | null;

export interface LegalModalProps {
  /** Which modal is currently open (or null if closed) */
  type: LegalModalType;
  /** Close callback */
  onClose: () => void;
  /** Restaurant metadata */
  restaurant: Restaurant;
  /** Optional language override */
  locale?: string;
}

export function LegalModal({ type, onClose, restaurant }: LegalModalProps) {
  const { consent, updateConsent, acceptAll, rejectAll } = useConsent();
  const { t } = useTranslation();

  // Close on Escape key
  useEffect(() => {
    if (!type) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [type, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [type]);

  if (!type || typeof document === 'undefined') return null;

  const modalTitle =
    type === 'privacy'
      ? t('footer.privacy', 'Privacy Policy')
      : type === 'terms'
        ? t('footer.terms', 'Terms of Service')
        : type === 'accessibility'
          ? t('footer.accessibility', 'Accessibility Statement')
          : type === 'cookies'
            ? t('footer.cookies', 'Cookie Settings')
            : '';

  const fullAddress = `${restaurant.address.streetAddress}, ${restaurant.address.postalCode} ${restaurant.address.addressLocality}, ${restaurant.address.addressCountry}`;

  return createPortal(
    <div className="legal-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="legal-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="legal-modal-header">
          <h2 id="legal-modal-title" className="legal-modal-title">
            {modalTitle}
          </h2>
          <button
            type="button"
            className="legal-modal-close"
            onClick={onClose}
            aria-label={t('close', 'Close')}
          >
            <CloseIcon width={22} height={22} />
          </button>
        </div>

        {/* Content Body */}
        <div className="legal-modal-body">
          {type === 'privacy' && (
            <PrivacyContent restaurant={restaurant} address={fullAddress} />
          )}
          {type === 'terms' && <TermsContent restaurant={restaurant} />}
          {type === 'accessibility' && (
            <AccessibilityContent restaurant={restaurant} />
          )}
          {type === 'cookies' && (
            <CookieSettingsContent
              consent={consent}
              onUpdate={updateConsent}
              onAcceptAll={() => {
                acceptAll();
                onClose();
              }}
              onRejectAll={() => {
                rejectAll();
                onClose();
              }}
              onSave={onClose}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// -----------------------------------------------------------------------------
// Privacy Policy Content (i18n-driven)
// -----------------------------------------------------------------------------
function PrivacyContent({
  restaurant,
  address,
}: {
  restaurant: Restaurant;
  address: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="legal-prose">
      <p className="legal-lead">
        {t('legal.privacy.lead', { name: restaurant.name })}
      </p>

      <h3>{t('legal.privacy.controller.title')}</h3>
      <p>
        {t('legal.privacy.controller.text', {
          name: restaurant.name,
          address,
          email: restaurant.email,
          phone: restaurant.phone,
        })}
      </p>

      <h3>{t('legal.privacy.collect.title')}</h3>
      <p>{t('legal.privacy.collect.intro')}</p>
      <ul>
        <li>{t('legal.privacy.collect.inquiries')}</li>
        <li>{t('legal.privacy.collect.reservations')}</li>
        <li>{t('legal.privacy.collect.analytics')}</li>
      </ul>

      <h3>{t('legal.privacy.purpose.title')}</h3>
      <p>{t('legal.privacy.purpose.text')}</p>

      <h3>{t('legal.privacy.retention.title')}</h3>
      <p>{t('legal.privacy.retention.text')}</p>

      <h3>{t('legal.privacy.rights.title')}</h3>
      <p>{t('legal.privacy.rights.text', { email: restaurant.email })}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Terms of Service Content (i18n-driven)
// -----------------------------------------------------------------------------
function TermsContent({ restaurant }: { restaurant: Restaurant }) {
  const { t } = useTranslation();

  return (
    <div className="legal-prose">
      <p className="legal-lead">{t('legal.terms.lead', { name: restaurant.name })}</p>

      <h3>{t('legal.terms.reservations.title')}</h3>
      <p>{t('legal.terms.reservations.text')}</p>

      <h3>{t('legal.terms.allergens.title')}</h3>
      <p>{t('legal.terms.allergens.text')}</p>

      <h3>{t('legal.terms.pricing.title')}</h3>
      <p>{t('legal.terms.pricing.text')}</p>

      <h3>{t('legal.terms.ip.title')}</h3>
      <p>{t('legal.terms.ip.text', { name: restaurant.name })}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Accessibility Statement Content (i18n-driven)
// -----------------------------------------------------------------------------
function AccessibilityContent({ restaurant }: { restaurant: Restaurant }) {
  const { t } = useTranslation();

  return (
    <div className="legal-prose">
      <p className="legal-lead">
        {t('legal.accessibility.lead', { name: restaurant.name })}
      </p>

      <h3>{t('legal.accessibility.highlights.title')}</h3>
      <ul>
        <li>{t('legal.accessibility.highlights.keyboard')}</li>
        <li>{t('legal.accessibility.highlights.screenreader')}</li>
        <li>{t('legal.accessibility.highlights.motion')}</li>
      </ul>

      <h3>{t('legal.accessibility.feedback.title')}</h3>
      <p>{t('legal.accessibility.feedback.text', { email: restaurant.email })}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Granular Cookie Settings Content (i18n-driven)
// -----------------------------------------------------------------------------
function CookieSettingsContent({
  consent,
  onUpdate,
  onAcceptAll,
  onRejectAll,
  onSave,
}: {
  consent: ConsentState;
  onUpdate: (updates: Partial<Omit<ConsentState, 'necessary'>>) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();

  const analyticsLabel = t('cookies.settings.analytics.title');
  const marketingLabel = t('cookies.settings.marketing.title');

  return (
    <div className="cookie-settings-panel">
      <p className="legal-lead">{t('cookies.settings.lead')}</p>

      <div className="cookie-category-list">
        {/* Necessary */}
        <div className="cookie-category-item">
          <div className="cookie-category-header">
            <div>
              <span className="cookie-category-title">
                {t('cookies.settings.necessary.title')}
              </span>
              <span className="cookie-category-badge">
                {t('cookies.settings.necessary.badge')}
              </span>
            </div>
          </div>
          <p className="cookie-category-desc">
            {t('cookies.settings.necessary.desc')}
          </p>
        </div>

        {/* Analytics */}
        <div className="cookie-category-item">
          <div className="cookie-category-header">
            <span className="cookie-category-title">{analyticsLabel}</span>
            <label className="cookie-switch" aria-label={analyticsLabel}>
              <input
                type="checkbox"
                checked={consent.analytics}
                onChange={(e) => onUpdate({ analytics: e.target.checked })}
              />
              <span className="cookie-slider" />
            </label>
          </div>
          <p className="cookie-category-desc">
            {t('cookies.settings.analytics.desc')}
          </p>
        </div>

        {/* Marketing / Functional */}
        <div className="cookie-category-item">
          <div className="cookie-category-header">
            <span className="cookie-category-title">{marketingLabel}</span>
            <label className="cookie-switch" aria-label={marketingLabel}>
              <input
                type="checkbox"
                checked={consent.functional}
                onChange={(e) => onUpdate({ functional: e.target.checked })}
              />
              <span className="cookie-slider" />
            </label>
          </div>
          <p className="cookie-category-desc">
            {t('cookies.settings.marketing.desc')}
          </p>
        </div>
      </div>

      <div className="cookie-settings-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={onRejectAll}
        >
          {t('cookies.settings.rejectAll')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={onSave}
        >
          {t('cookies.settings.save')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={onAcceptAll}
        >
          {t('cookies.settings.acceptAll')}
        </button>
      </div>
    </div>
  );
}
